import type { ClaimCheckDefInput } from '@cyberscore/contracts';
import {
  optionPolicy,
  QuestionDefinition,
  RuleDefinition,
  type QuestionType,
  type TraceNode,
} from '@cyberscore/contracts';
import type { Cond } from './conditions';
import type { EffectDraft } from './EffectsEditor';
import type { ItemSummary } from './journey';

/** The question and rule editors' logic (task 43): drafts as stored (input shape), checked by the contracts. */

export interface OptionDraft {
  value: string;
  label: string;
  effects?: EffectDraft[];
}

export interface QuestionDraft {
  text: string;
  help?: string;
  type: QuestionType;
  mandatory?: boolean;
  visibility?: Cond;
  /** Asked only when a chosen insurer form reads it (task 96). */
  for_forms?: boolean;
  depends_on?: string[];
  options?: OptionDraft[];
  effects?: EffectDraft[];
  settings?: Record<string, unknown>;
  metadata?: { risk_domain?: string; controls?: string[]; kill_switch?: boolean } & Record<string, unknown>;
  /** Checks of the answer against the business's documents (task 62). */
  checks?: ClaimCheckDefInput[];
  [other: string]: unknown;
}

export interface RuleDraft {
  name: string;
  description?: string;
  enabled?: boolean;
  when: Cond;
  then: EffectDraft[];
}

export interface Issue {
  path: string;
  message: string;
}

const issues = (r: {
  success: boolean;
  error?: { issues: { path: PropertyKey[]; message: string }[] };
}): Issue[] =>
  r.success ? [] : r.error!.issues.map((i) => ({ path: i.path.map(String).join('.'), message: i.message }));

/** What would stop the question being saved: the same check the core runs. */
export const questionIssues = (q: QuestionDraft) => issues(QuestionDefinition.safeParse(q));
export const ruleIssues = (r: RuleDraft) => issues(RuleDefinition.safeParse(r));

/** The parsed question (defaults filled in) for the live preview, or null while it doesn't parse. */
export function previewQuestion(q: QuestionDraft) {
  const r = QuestionDefinition.safeParse(q);
  return r.success ? r.data : null;
}

/** An option value from its label: lowercase letters, digits and _. */
export function slug(label: string): string {
  return label.toLowerCase().normalize('NFKD').replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 60) || 'option'; // prettier-ignore
}

/** A value not used by another option yet. */
export function freeValue(options: readonly OptionDraft[], label: string): string {
  const taken = new Set(options.map((o) => o.value));
  const base = slug(label);
  let v = base;
  for (let i = 2; taken.has(v); i++) v = `${base}_${i}`;
  return v;
}

/**
 * The question with a new type: settings reset (each type has its own), options dropped where the type
 * takes none and started where it needs them; a boolean gets Yes and No.
 */
export function withType(q: QuestionDraft, type: QuestionType): QuestionDraft {
  const policy = optionPolicy(type);
  let options = q.options ?? [];
  if (policy === 'none') options = [];
  else if (type === 'boolean')
    options = [
      { value: 'yes', label: 'Yes' },
      { value: 'no', label: 'No' },
    ];
  else if (policy === 'required')
    while (options.length < 2) {
      const label = `Option ${options.length + 1}`;
      options = [...options, { value: freeValue(options, label), label }];
    }
  return { ...q, type, settings: {}, options };
}

export function newQuestion(): QuestionDraft {
  return { text: 'New question', type: 'single_choice', mandatory: true, options: [{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }] }; // prettier-ignore
}

export function newRule(question?: string): RuleDraft {
  return {
    name: 'New rule',
    enabled: true,
    when: question ? { question, op: 'answered' } : { all: [] },
    then: [{ type: 'require_human_review', params: { reason: '' } }],
  };
}

/** Items matching a search over key and title, one row per key (the author's own variant first). */
export function searchItems<T extends ItemSummary>(items: readonly T[], query: string): T[] {
  const q = query.trim().toLowerCase();
  if (!q) return [...items];
  return items.filter((i) => i.key.toLowerCase().includes(q) || (i.title ?? '').toLowerCase().includes(q));
}

/** A trace leaf's value in words. */
export function showActual(n: Extract<TraceNode, { kind: 'test' }>): string {
  if (n.ignored === 'hidden') return 'answered, but the question is hidden';
  if (n.ignored === 'provenance') return `answered (${n.provenance}), but not from an accepted source`;
  if (n.actual === undefined) return 'no answer';
  return `saw ${typeof n.actual === 'string' ? n.actual : JSON.stringify(n.actual)}`;
}
